// The flow's position lives in the URL: one route per station, so the
// browser's history IS the flow's history. One landing rule, arrival and
// in-session alike: land on the station the URL asks for, capped at the
// step the SERVER derives — nothing ahead of the derivation can render,
// and everything behind it renders in its current state (read-only once
// the application completed). A converted quote clamps every ask to
// /done, in both directions: there is no avenue back once the policy
// issued, and repeated Back walks out of the flow the way it came in.

import type { QuoteFlowStepName } from "./analytics";

/** The stations in walk order. `account` is a free station: it stands last
 * in the list only so the order is total, and every comparison below
 * treats it as reachable at any time and never ahead of anything. `review`
 * is client-owned — the server's
 * derived step never names it, so mid-walk it caps back to the
 * application (which re-settles onto its review face by itself), and it
 * only stands as an address of its own once the application completed. */
const STATION_ORDER: readonly QuoteFlowStepName[] = [
  "quiz",
  "schedule-call",
  "quote",
  "application",
  "review",
  "payment",
  "sign",
  "finalize",
  "done",
  "account",
];

function stationIndex(station: QuoteFlowStepName): number {
  return STATION_ORDER.indexOf(station);
}

/** Whether `station` stands later in the order than `of` — the same
 * comparison `landingStation` makes internally, exposed for callers that
 * need to reason about a station's position without duplicating the
 * order (the quiz's own ahead-of-derivation check, for one). */
export function stationIsAhead(
  station: QuoteFlowStepName,
  of: QuoteFlowStepName,
): boolean {
  if (station === "account" || of === "account") return false;
  return stationIndex(station) > stationIndex(of);
}

/** The URL for a station — the quote screen is the bare quote URL, every
 * later station a segment under it; a walk screen is a segment under the
 * application's. */
export function stationPath(
  quoteId: string,
  station: QuoteFlowStepName,
  screenRef?: string | null,
): string {
  if (station === "quote") return `/quotes/${quoteId}`;
  if ((station === "application" || station === "quiz") && screenRef) {
    return `/quotes/${quoteId}/${station}/${screenRef}`;
  }
  return `/quotes/${quoteId}/${station}`;
}

/** The station a URL's step segment asks for: none means the quote
 * screen; a segment naming nothing (or spelling out "quote") is null,
 * and the flow replaces it with where the family stands. */
export function requestedStation(
  segment: string | undefined,
): QuoteFlowStepName | null {
  if (segment === undefined) return "quote";
  if (segment === "quote") return null;
  return STATION_ORDER.includes(segment as QuoteFlowStepName)
    ? (segment as QuoteFlowStepName)
    : null;
}

/** The server's derived step, translated to the station vocabulary. An
 * unknown value falls back to the quote screen — and can never fall
 * backward past a milestone, because the derivation's own vocabulary is
 * closed and every member is mapped here. */
export function serverStation(serverStep: string): QuoteFlowStepName {
  if (serverStep === "quiz") return "quiz";
  if (serverStep === "schedule_call") return "schedule-call";
  if (serverStep === "application") return "application";
  if (serverStep === "payment_method") return "payment";
  if (serverStep === "sign") return "sign";
  if (serverStep === "finalize") return "finalize";
  if (serverStep === "complete" || serverStep === "closed") return "done";
  return "quote";
}

/**
 * Where a URL asking for `requested` lands, given the server's derived
 * station: the ask, capped at the derivation. Behind the cap everything
 * renders (in its current state); ahead of it nothing can, so the ask
 * redirects forward. A `done` derivation clamps BOTH directions — the
 * flow is over, and no station behind it exists to revisit. Two asks
 * stand ahead of their derivation by design: the application opens
 * without a selection (the schedule path walks in before any choice
 * lands), and the review face is the application's own tail — both
 * render the application step, which settles itself.
 *
 * `schedule-call` stands apart from the behind/ahead rule everything else
 * follows: it renders only ON the derivation, never revisited once the
 * family has moved past it and never anticipated before the server calls
 * for it — a stale schedule-call bookmark always redirects to wherever
 * the family actually stands. A `quiz` ask past the derivation is NOT
 * capped here (a `quiz` request always stands "behind" every other
 * station in the order, so the rule below would already let it through);
 * whether it should still be allowed to render depends on whether any
 * quiz answers are on hand, which lives outside this pure function — see
 * the call site.
 */
export function landingStation(
  requested: QuoteFlowStepName,
  derived: QuoteFlowStepName,
): QuoteFlowStepName {
  if (requested === "account") return "account";
  if (derived === "done") return "done";
  if (requested === "schedule-call") {
    return derived === "schedule-call" ? "schedule-call" : derived;
  }
  if (requested === "application" && derived === "quote") return "application";
  if (requested === "review" && derived === "application") return "review";
  return stationIndex(requested) > stationIndex(derived) ? derived : requested;
}

/**
 * The server-step spelling of a station the client just watched the
 * server accept — for patching a view whose follow-up read failed, so
 * the landing rule caps against the milestone that genuinely stands.
 */
export function serverStepFor(station: QuoteFlowStepName): string {
  if (station === "payment") return "payment_method";
  if (station === "done") return "complete";
  if (station === "schedule-call") return "schedule_call";
  return station;
}
